import { call, put, takeLatest, debounce } from 'redux-saga/effects';
import * as T from './reservationReportConstants';
import * as actions from './reservationReportActions';
import {
  getReservationSummaryApi,
  getReservationCheckinKpiApi,
  getReservationLogApi,
  getReservationDetailApi,
  getReservationByStatusApi,
  getReservationHourlyApi,
  getTodayNewDetailsApi,
} from './reservationReportApi';

function addOneDay(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function* fetchReservationSummarySaga(
  action: ReturnType<typeof actions.fetchReservationSummaryRequest>
) {
  try {
    const response: any = yield call(getReservationSummaryApi, action.payload.locationId, action.payload.date);
    yield put(actions.fetchReservationSummarySuccess(response));
  } catch (error: any) {
    yield put(actions.fetchReservationSummaryFailure(error.message || 'Failed to fetch reservation summary'));
  }
}

function* fetchReservationCheckinKpiSaga(
  action: ReturnType<typeof actions.fetchReservationCheckinKpiRequest>
) {
  try {
    const response: any = yield call(getReservationCheckinKpiApi, action.payload.locationId, action.payload.date);
    yield put(actions.fetchReservationCheckinKpiSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchReservationCheckinKpiFailure(error.message || 'Failed to fetch checkin KPIs'));
  }
}

function* fetchReservationLogSaga(
  action: ReturnType<typeof actions.fetchReservationLogRequest>
) {
  try {
    const response: any = yield call(getReservationLogApi, action.payload);
    yield put(actions.fetchReservationLogSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchReservationLogFailure(error.message || 'Failed to fetch reservation log'));
  }
}

function* fetchReservationDetailSaga(
  action: ReturnType<typeof actions.fetchReservationDetailRequest>
) {
  try {
    const response: any = yield call(getReservationDetailApi, action.payload);
    yield put(actions.fetchReservationDetailSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchReservationDetailFailure(error.message || 'Failed to fetch reservation detail'));
  }
}

function* fetchReservationByStatusSaga(
  action: ReturnType<typeof actions.fetchReservationByStatusRequest>
) {
  try {
    const response: any = yield call(getReservationByStatusApi, action.payload.locationId, action.payload.date);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchReservationByStatusSuccess(data));
  } catch (error: any) {
    yield put(actions.fetchReservationByStatusFailure(error.message || 'Failed to fetch reservations by status'));
  }
}

function* fetchReservationHourlySaga(
  action: ReturnType<typeof actions.fetchReservationHourlyRequest>
) {
  try {
    const response: any = yield call(getReservationHourlyApi, action.payload.locationId, action.payload.date);
    const data = Array.isArray(response) ? response : (response?.data ?? []);
    yield put(actions.fetchReservationHourlySuccess(data));
  } catch (error: any) {
    yield put(actions.fetchReservationHourlyFailure(error.message || 'Failed to fetch hourly reservations'));
  }
}

function* fetchTodayNewDetailsSaga(
  action: ReturnType<typeof actions.fetchTodayNewDetailsRequest>
) {
  try {
    const { startDate, endDate, ...rest } = action.payload;
    const adjustedEndDate = startDate === endDate ? addOneDay(endDate) : endDate;
    const response: any = yield call(getTodayNewDetailsApi, { startDate, endDate: adjustedEndDate, ...rest });
    yield put(actions.fetchTodayNewDetailsSuccess(response));
  } catch (error: any) {
    yield put(actions.fetchTodayNewDetailsFailure(error.message || 'Failed to fetch today reservations'));
  }
}

export default function* reservationReportSaga() {
  yield takeLatest(T.FETCH_RESERVATION_SUMMARY_REQUEST, fetchReservationSummarySaga);
  yield takeLatest(T.FETCH_RESERVATION_CHECKIN_KPI_REQUEST, fetchReservationCheckinKpiSaga);
  yield takeLatest(T.FETCH_RESERVATION_LOG_REQUEST, fetchReservationLogSaga);
  yield takeLatest(T.FETCH_RESERVATION_DETAIL_REQUEST, fetchReservationDetailSaga);
  yield takeLatest(T.FETCH_RESERVATION_BY_STATUS_REQUEST, fetchReservationByStatusSaga);
  yield takeLatest(T.FETCH_RESERVATION_HOURLY_REQUEST, fetchReservationHourlySaga);
  yield debounce(400, T.FETCH_TODAY_NEW_DETAILS_REQUEST, fetchTodayNewDetailsSaga);
}
